import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import curbsideAPIService from '../services/curbsideAPIService';
import { toast } from 'react-toastify';

export const saveOrderAction = createAsyncThunk(
  'orderActions/saveOrderAction',
  async ({ dob, manual, eventType, orderId }: { dob: string; manual: boolean; eventType: string; orderId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await curbsideAPIService.post('logevent', {
        dob,
        manual,
        eventType,
        orderId,
      });
      const sortConfig = { key: 'timestamp', direction: 'desc' }; // Default sortConfig or retrieve from state
      dispatch(fetchOrderLogs({ orderId, sortConfig }));
      return response;
    } catch (error) {
      console.error('Error in saveOrderAction:', error);
      const errorMessage =
        (error as any)?.response?.data?.message ?? 'An unexpected error occurred while saving the order action.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const verifyDOB = createAsyncThunk(
  'orderActions/verifyDOB',
  async ({ dob, orderId }: { dob: string; orderId: string }, { dispatch, rejectWithValue }) => {
    try {
      const response = await curbsideAPIService.post('verifyage', {
        dob,
        manual: true,
        OrderID: orderId,
      });
      dispatch(fetchOrderLogs({ orderId, sortConfig : { key: 'timestamp', direction: 'desc' } }));
      return response;
    } catch (error) {
      console.error('Error verifying DOB:', error);
      const errorMessage = (error as any)?.response?.data?.message ?? 'Failed to save DOB. Please try again.';
      return rejectWithValue(errorMessage);
    }
  }
);

export const fetchOrderLogs = createAsyncThunk(
  'orderActions/fetchOrderLogs',
  async ({ orderId, sortConfig }: { orderId: string; sortConfig: { key: string; direction: string } }, { rejectWithValue }) => {
    const endpoint = 'orderlogs';
    const data = {
      orderID: orderId,
      sortBy: sortConfig.key,
      sortDirection: sortConfig.direction,
    };
    try {
      const response = await curbsideAPIService.post(endpoint, data);
      return response;
    } catch (error) {
      console.error('Error fetching order logs:', error);
      const errorMessage = (error as any)?.response?.data?.message ?? 'An unexpected error occurred while fetching order logs.';
      return rejectWithValue(errorMessage);
    }
  }
);

const orderDetailsSlice = createSlice({
  name: 'orderDetails',
  initialState: {
    orderLogData: [],
    loading: false,
    sortConfig: {
      key: 'timestamp',
      direction: 'desc',
    },
    showScanner: false,
    showDOBModal: false,
    showCancelOrderModal: false,
    showCompleteOrderModal: false,
    showFlaggedOrderModal: false,
    showReadyToPickupModal: false,
    manual: false,
    allowOrderCompletion: false,
    allowIDScanning: false
  },

  reducers: {
    setModalVisibility: (state, action) => {
      const { modal, visible } = action.payload;
      return {
        ...state,
        [modal]: visible,
      };
    },
    setLoading: (state, action) => {
      return {
        ...state,
        loading: action.payload,
      };
    },
    setShowDOBModal: (state, action) => {
      return {
        ...state,
        showDOBModal: action.payload,
      };
    },
    setShowScanner: (state, action) => {
      return {
        ...state,
        showScanner: action.payload,
      };
    },
    setManual: (state, action) => {
      return {
        ...state,
        manual: action.payload,
      };
    },
    setAllowOrderCompletion: (state, action) => {
      return {
        ...state,
        allowOrderCompletion: action.payload,
      };
    },
    setAllowIDScanning: (state, action) => {
      return {
        ...state,
        allowIDScanning: action.payload,
      };
    },
  },

  extraReducers: (builder) => {
    builder
      .addCase(saveOrderAction.pending, (state) => {
        return {
          ...state,
          loading: true,
        };
      })
      .addCase(saveOrderAction.fulfilled, (state, action) => {
        const messages: Record<string, string> = {
          FlagAsOverdue: 'Order flagged as overdue successfully.',
          CancelOrder: 'Order canceled successfully.',
          CompleteOrder: 'Order completed successfully.',
          SendReadyforPickupNotification: 'Order ready for pickup.',
        };

        const eventType = action.payload.eventType as keyof typeof messages;
        toast.success(messages[eventType]);
      })
      .addCase(saveOrderAction.rejected, (state, action) => {
        toast.error(action.payload as string);
      })
      .addCase(verifyDOB.fulfilled, (state, action) => {
        const response = action.payload;
        
        let allowCompletion = false;
        let allowIDScanning = false;

        // response.data is a JSON-string, so parse it
        try {
          const parsed = JSON.parse(response.data);
          allowCompletion = parsed.allowCompletion === true;
          allowIDScanning = parsed.allowIDScanning === true;
        } catch (e) {
          console.error('Failed to parse age-verify payload:', e);
        }

        // store it in your slice
        state.allowOrderCompletion = allowCompletion;
        state.allowIDScanning = allowIDScanning;

        // give feedback based on the flag
        if (allowCompletion) {
          toast.success('Order has been age verified successfully');
        } else {
          toast.error('Age verification failed. Customer too young to complete order.');
        }
      })
      .addCase(verifyDOB.rejected, (state, action) => {
        toast.error(action.payload as string );
      })

      .addCase(fetchOrderLogs.fulfilled, (state, action) => {
        return {
          ...state,
          orderLogData: action.payload,
        };
      })
      .addCase(fetchOrderLogs.rejected, (state, action) => {
        toast.error(action.payload as string);
      });
  },
});

export const { setModalVisibility, setLoading, setShowDOBModal, setShowScanner, setManual, setAllowOrderCompletion, setAllowIDScanning } = orderDetailsSlice.actions;
export default orderDetailsSlice.reducer;


